import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getStockQuote, type StockQuote } from '../services/stockApi';
import { adminApi, isUnauthorized } from '../services/adminApi';
import stocksList from '../data/stocksList.json';
import { ensureSymbolColorAssigned } from '../utils/symbolColors';

type Mode = 'add' | 'edit';

type EditRowSnapshot = {
  symbol: string;
  currentPrice: number;
  changePct: number;
  buyPriceAlert: number | null;
  sellPriceAlert: number | null;
};

type StockListItem = {
  symbol: string;
  name: string;
  exchange: string;
  category: string;
};

export type WatchlistAddEditModalProps = {
  isDarkMode: boolean;
  open: boolean;
  mode: Mode;
  titleEdit?: string;
  titleAdd?: string;
  footerLabel?: string;
  prefillSymbol?: string | null;
  editRow?: EditRowSnapshot | null;
  existingSymbols?: Set<string>;
  authToken?: string | null;
  onUnauthorized?: () => void;
  onClose: () => void;
  onSaved?: (symbol: string) => void;
};

const normalizeSymbol = (raw: string): string => String(raw || '').trim().toUpperCase();

const computeAlertStatus = (currentPrice: number, buyPriceAlert: number | null, sellPriceAlert: number | null): string => {
  const price = Number(currentPrice);
  const buy = buyPriceAlert == null ? NaN : Number(buyPriceAlert);
  const sell = sellPriceAlert == null ? NaN : Number(sellPriceAlert);

  const hasBuy = Number.isFinite(buy) && buy !== 0;
  const hasSell = Number.isFinite(sell) && sell !== 0;
  if (!hasBuy && !hasSell) return '';

  if (!Number.isFinite(price)) return 'Not Reached';
  if (hasSell && sell < price) return 'Sell Triggered';
  if (hasBuy && price < buy) return 'Buy Triggered';
  return 'Not Reached';
};

const parseNumberOrNull = (raw: string): number | null => {
  const t = String(raw || '').trim();
  if (!t) return null;

  let cleaned = t.replace(/\s+/g, '');
  cleaned = cleaned.replace(/[$₪€£]/g, '');

  const hasComma = cleaned.includes(',');
  const hasDot = cleaned.includes('.');
  if (hasComma && !hasDot) cleaned = cleaned.replace(',', '.');
  else if (hasComma && hasDot) cleaned = cleaned.replace(/,/g, '');

  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

const WatchlistAddEditModal: React.FC<WatchlistAddEditModalProps> = ({
  isDarkMode,
  open,
  mode,
  titleEdit,
  titleAdd,
  prefillSymbol,
  editRow,
  existingSymbols,
  authToken,
  onUnauthorized,
  onClose,
  onSaved,
}) => {
  const colors = useMemo(() => {
    const dark = isDarkMode;
    return {
      pageBg: dark ? '#111827' : '#ffffff',
      text: dark ? '#e5e7eb' : '#111827',
      subText: dark ? '#9ca3af' : '#6b7280',
      cardBg: dark ? '#0b1220' : '#ffffff',
      border: dark ? 'rgba(148,163,184,0.18)' : '#e5e7eb',
      headerBg: dark ? 'rgba(255,255,255,0.03)' : '#f9fafb',
      hoverBg: dark ? 'rgba(255,255,255,0.04)' : '#f3f4f6',
      buttonBg: dark ? '#111827' : '#ffffff',
      buttonBorder: dark ? 'rgba(148,163,184,0.28)' : '#93c5fd',
      buttonText: dark ? '#93c5fd' : '#2563eb',
      menuBg: dark ? '#0b1220' : '#ffffff',
      menuShadow: dark ? '0 18px 46px rgba(0,0,0,0.65)' : '0 18px 46px rgba(15,23,42,0.18)',
      buy: '#ef4444',
      sell: '#f97316',
      neutral: dark ? '#94a3b8' : '#9ca3af',
    };
  }, [isDarkMode]);

  const [addSymbol, setAddSymbol] = useState('');
  const [addBuyAlert, setAddBuyAlert] = useState('');
  const [addSellAlert, setAddSellAlert] = useState('');
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const [localExistingSymbols, setLocalExistingSymbols] = useState<Set<string> | null>(null);

  const [showAddAutocomplete, setShowAddAutocomplete] = useState(false);
  const [addSelectedIndex, setAddSelectedIndex] = useState(-1);
  const addAutocompleteRef = useRef<HTMLDivElement | null>(null);
  const addInputRef = useRef<HTMLInputElement | null>(null);
  const modalRef = useRef<HTMLDivElement | null>(null);
  const lastFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    if (mode === 'edit') {
      setAddSymbol(normalizeSymbol(editRow?.symbol || ''));
      setAddBuyAlert(String(editRow?.buyPriceAlert ?? ''));
      setAddSellAlert(String(editRow?.sellPriceAlert ?? ''));
    } else {
      const prefill = prefillSymbol ? normalizeSymbol(prefillSymbol) : '';
      setAddSymbol(prefill);
      setAddBuyAlert('');
      setAddSellAlert('');
    }

    setAddBusy(false);
    setAddError(null);
    setShowAddAutocomplete(false);
    setAddSelectedIndex(-1);

    if (existingSymbols) {
      setLocalExistingSymbols(existingSymbols);
    } else {
      setLocalExistingSymbols(null);
    }
  }, [open, mode, prefillSymbol, editRow, existingSymbols]);

  // Best-effort: for global usage (no existingSymbols provided), fetch watchlist symbols silently.
  useEffect(() => {
    if (!open) return;
    if (mode !== 'add') return;
    if (existingSymbols) return;
    if (localExistingSymbols) return;
    if (!authToken) return;

    let cancelled = false;
    void (async () => {
      try {
        const items = await adminApi.listMyWatchlist(authToken);
        if (cancelled) return;
        const set = new Set<string>((items ?? []).map((it) => normalizeSymbol(it.symbol)));
        setLocalExistingSymbols(set);
      } catch (err) {
        if (cancelled) return;
        if (isUnauthorized(err)) {
          onUnauthorized?.();
          return;
        }
        // Ignore; duplicate detection will just be unavailable.
        setLocalExistingSymbols(new Set());
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, mode, authToken, existingSymbols, localExistingSymbols, onUnauthorized]);

  const duplicateAddSymbol = useMemo(() => {
    if (mode !== 'add') return null as string | null;
    const sym = normalizeSymbol(addSymbol);
    if (!sym) return null;
    if (!localExistingSymbols) return null;
    return localExistingSymbols.has(sym) ? sym : null;
  }, [addSymbol, mode, localExistingSymbols]);

  const uniqueStocksList = useMemo(() => {
    const list = stocksList as StockListItem[];
    const excluded = new Set(['MAG7']);
    const seen = new Set<string>();
    return list.filter((stock) => {
      if (excluded.has(stock.symbol)) return false;
      const key = String(stock.symbol || '').trim().toUpperCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, []);

  const addFilteredStocks = useMemo(() => {
    const raw = addSymbol.trim();
    if (!raw) return [] as StockListItem[];

    const query = raw.toLowerCase();
    const querySym = raw.toUpperCase();

    const score = (stock: StockListItem): number => {
      const sym = (stock.symbol || '').toUpperCase();
      const name = (stock.name || '').toLowerCase();
      const cat = (stock.category || '').toLowerCase();

      if (sym === querySym) return 1000;
      if (sym.startsWith(querySym)) return 900;
      if (sym.includes(querySym)) return 800;
      if (name.startsWith(query)) return 700;
      if (name.includes(query)) return 600;
      if (cat.includes(query)) return 500;
      return 0;
    };

    return uniqueStocksList
      .map((stock) => ({ stock, s: score(stock) }))
      .filter((row) => row.s > 0)
      .sort((a, b) => (b.s - a.s) || a.stock.symbol.localeCompare(b.stock.symbol))
      .slice(0, 10)
      .map((row) => row.stock);
  }, [addSymbol, uniqueStocksList]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        addAutocompleteRef.current &&
        !addAutocompleteRef.current.contains(event.target as Node) &&
        addInputRef.current &&
        !addInputRef.current.contains(event.target as Node)
      ) {
        setShowAddAutocomplete(false);
        setAddSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement | null;
      const modal = modalRef.current;
      if (!modal || !target) return;

      if (modal.contains(target)) {
        lastFocusedRef.current = target;
        return;
      }

      const fallback = modal.querySelector<HTMLElement>(
        'input, button, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      (lastFocusedRef.current ?? fallback)?.focus();
    };

    document.addEventListener('focusin', onFocusIn, true);
    return () => document.removeEventListener('focusin', onFocusIn, true);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (mode !== 'add') return;
    if (prefillSymbol) return;

    // Focus only when not prefilled (avoids auto-opening autocomplete).
    const t = window.setTimeout(() => {
      addInputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [open, mode, prefillSymbol]);

  const runSearch = async (symbolOverride?: string) => {
    const sym = normalizeSymbol(symbolOverride ?? addSymbol);
    if (!sym) {
      setAddError('Please enter a stock symbol.');
      return;
    }

    setAddBusy(true);
    setAddError(null);
    try {
      const q = await getStockQuote(sym);
      if (!q || !Number.isFinite(Number(q.price))) {
        setAddError('Quote not found (or API key missing).');
        return;
      }
    } catch {
      setAddError('Failed to fetch quote.');
    } finally {
      setAddBusy(false);
    }
  };

  const save = async () => {
    const sym = normalizeSymbol(addSymbol);
    if (!sym) {
      setAddError('Please enter a stock symbol.');
      return;
    }

    if (mode === 'add' && duplicateAddSymbol) {
      setAddError(`${sym} already exists in My Watchlist. Use Actions → Edit to update it.`);
      return;
    }

    if (!authToken) {
      setAddError('You must be logged in to save watchlist items.');
      return;
    }

    setAddBusy(true);
    setAddError(null);

    try {
      const nextBuyAlert = parseNumberOrNull(addBuyAlert);
      const nextSellAlert = parseNumberOrNull(addSellAlert);

      let q: StockQuote | null = null;
      try {
        q = await getStockQuote(sym);
      } catch {
        q = null;
      }

      const quotePrice = q ? Number(q.price) : NaN;
      const quoteChange = q ? Number(q.changesPercentage) : NaN;

      const isAdd = mode === 'add';
      if (isAdd && (!q || !Number.isFinite(quotePrice))) {
        setAddError('Quote not found (or API key missing).');
        return;
      }

      const existing = mode === 'edit' ? editRow ?? null : null;

      const effectivePrice = Number.isFinite(quotePrice)
        ? quotePrice
        : Number.isFinite(existing?.currentPrice)
          ? Number(existing?.currentPrice)
          : 0;

      const status = computeAlertStatus(
        effectivePrice,
        nextBuyAlert,
        nextSellAlert
      );

      await adminApi.upsertMyWatchlistItem(authToken, {
        symbol: sym,
        buyPriceAlert: nextBuyAlert,
        sellPriceAlert: nextSellAlert,
        currentPrice: Number.isFinite(quotePrice) ? quotePrice : existing?.currentPrice ?? null,
        changePct: Number.isFinite(quoteChange) ? quoteChange : existing?.changePct ?? null,
        status,
      });

      // Assign/persist a palette color for this symbol.
      ensureSymbolColorAssigned(sym);

      onSaved?.(sym);
      onClose();
    } catch (err) {
      if (isUnauthorized(err)) {
        onUnauthorized?.();
        return;
      }
      setAddError(err instanceof Error ? err.message : 'Failed to save watchlist item.');
    } finally {
      setAddBusy(false);
    }
  };

  if (!open) return null;

  const modalOverlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: isDarkMode ? 'rgba(0,0,0,0.55)' : 'rgba(15,23,42,0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 18,
    zIndex: 6000,
  };

  const modalStyle: React.CSSProperties = {
    width: 432,
    height: 421,
    backgroundColor: colors.cardBg,
    border: `1px solid ${colors.border}`,
    borderRadius: 0,
    boxShadow: colors.menuShadow,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  const modalHeaderStyle: React.CSSProperties = {
    height: 56,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 16px',
  };

  const modalTitleStyle: React.CSSProperties = {
    fontSize: 14,
    fontWeight: 900,
    color: colors.text,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  };

  const addTitleTextStyle: React.CSSProperties = {
    fontFamily: 'Manrope, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial',
    fontWeight: 600,
    fontStyle: 'normal',
    fontSize: 20,
    lineHeight: '100%',
    letterSpacing: 0,
    color: isDarkMode ? '#F9FAFB' : '#000000',
  };

  const modalCloseStyle: React.CSSProperties = {
    width: 32,
    height: 32,
    border: 'none',
    background: 'transparent',
    padding: 0,
    cursor: 'pointer',
    color: isDarkMode ? '#F3F4F6' : '#0D0D0D',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const modalBodyStyle: React.CSSProperties = {
    padding: 16,
    display: 'grid',
    gap: 25,
    flex: 1,
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: 'Manrope',
    fontSize: 16,
    fontWeight: 400,
    fontStyle: 'normal',
    lineHeight: '100%',
    letterSpacing: 0,
    color: isDarkMode ? '#F3F4F6' : '#0D0D0D',
    marginBottom: 6,
  };

  const inputStyle: React.CSSProperties = {
    width: 220,
    minWidth: 220,
    maxWidth: 220,
    height: 40,
    borderRadius: 8,
    border: '1px solid #D9D9D9',
    backgroundColor: isDarkMode ? '#0b1220' : '#ffffff',
    color: colors.text,
    padding: '0 10px',
    outline: 'none',
    fontSize: 13,
    opacity: 1,
    boxSizing: 'border-box',
  };

  const autocompleteContainerStyle: React.CSSProperties = {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    width: 220,
    maxHeight: 220,
    overflowY: 'auto',
    backgroundColor: colors.menuBg,
    border: `1px solid ${colors.border}`,
    borderRadius: 10,
    boxShadow: colors.menuShadow,
    zIndex: 7000,
  };

  const autocompleteItemStyle = (isSelected: boolean): React.CSSProperties => ({
    padding: '10px 10px',
    cursor: 'pointer',
    backgroundColor: isSelected ? colors.hoverBg : 'transparent',
    borderBottom: `1px solid ${colors.border}`,
    transition: 'background-color 0.15s ease',
  });

  const autocompleteSymbolStyle: React.CSSProperties = {
    fontSize: 13,
    fontWeight: 900,
    color: colors.text,
  };

  const autocompleteNameStyle: React.CSSProperties = {
    fontSize: 12,
    color: colors.subText,
    marginTop: 2,
  };

  const primaryButtonStyle: React.CSSProperties = {
    width: 175,
    height: 37,
    padding: '4px 14px',
    borderRadius: 6,
    border: '1px solid #2F58F2',
    backgroundColor: isDarkMode ? '#3B82F6' : '#2F58F2',
    color: isDarkMode ? '#111827' : '#ffffff',
    fontWeight: 800,
    cursor: 'pointer',
    opacity: 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  };

  const searchButtonStyle: React.CSSProperties = {
    height: 38,
    padding: '0 16px',
    borderRadius: 8,
    border: `1px solid ${colors.border}`,
    backgroundColor: isDarkMode ? '#0b1220' : '#111827',
    color: isDarkMode ? colors.text : '#ffffff',
    fontWeight: 900,
    cursor: 'pointer',
    minWidth: 110,
  };

  const modalFooterStyle: React.CSSProperties = {
    padding: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  };

  const resolvedTitleEdit = titleEdit || 'Edit Stock in My Watchlist';
  const resolvedTitleAdd = titleAdd || 'Add a Stock to My Watchlist';
  return createPortal(
    <div
      style={modalOverlayStyle}
      role="dialog"
      aria-modal="true"
      aria-label={mode === 'edit' ? resolvedTitleEdit : resolvedTitleAdd}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div ref={modalRef} style={modalStyle} onMouseDown={(e) => e.stopPropagation()}>
        <div style={modalHeaderStyle}>
          <div style={modalTitleStyle}>
            <span style={{ color: '#2F58F2', fontWeight: 900, fontSize: 22, lineHeight: 1 }}>+</span>
            <span style={addTitleTextStyle}>
              {mode === 'edit' ? resolvedTitleEdit : resolvedTitleAdd}
            </span>
          </div>
          <button type="button" style={modalCloseStyle} onClick={onClose} aria-label="Close">
            <img src="/images/remove.svg" alt="Close" style={{ width: 24, height: 24, display: 'block' }} />
          </button>
        </div>

        <div style={modalBodyStyle}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 14, alignItems: 'end', width: '100%' }}>
            <div style={{ marginLeft: 5 }}>
              <div style={labelStyle}>Stock Name</div>
              <div style={{ position: 'relative', width: 220 }}>
                <input
                  ref={addInputRef}
                  value={addSymbol}
                  onChange={(e) => {
                    if (mode === 'edit') return;
                    const v = e.target.value;
                    setAddSymbol(v);
                    setAddError(null);
                    setShowAddAutocomplete(v.trim().length > 0);
                    setAddSelectedIndex(-1);
                  }}
                  placeholder="Search Symbol"
                  style={inputStyle}
                  disabled={mode === 'edit'}
                  onFocus={() => {
                    if (mode === 'edit') return;
                    if (prefillSymbol) return;
                    if (addSymbol.trim()) setShowAddAutocomplete(true);
                  }}
                  onKeyDown={(e) => {
                    if (mode === 'edit') return;
                    if (!showAddAutocomplete || addFilteredStocks.length === 0) {
                      if (e.key === 'Enter') void runSearch();
                      if (e.key === 'Escape') {
                        setShowAddAutocomplete(false);
                        setAddSelectedIndex(-1);
                      }
                      return;
                    }

                    switch (e.key) {
                      case 'ArrowDown':
                        e.preventDefault();
                        setAddSelectedIndex((prev) => (prev < addFilteredStocks.length - 1 ? prev + 1 : prev));
                        break;
                      case 'ArrowUp':
                        e.preventDefault();
                        setAddSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
                        break;
                      case 'Enter':
                        e.preventDefault();
                        if (addSelectedIndex >= 0 && addSelectedIndex < addFilteredStocks.length) {
                          const s = addFilteredStocks[addSelectedIndex].symbol;
                          setAddSymbol(s);
                          setShowAddAutocomplete(false);
                          setAddSelectedIndex(-1);
                          void runSearch(s);
                        } else {
                          void runSearch();
                        }
                        break;
                      case 'Escape':
                        setShowAddAutocomplete(false);
                        setAddSelectedIndex(-1);
                        break;
                    }
                  }}
                />

                {mode !== 'edit' && showAddAutocomplete && addFilteredStocks.length > 0 && (
                  <div ref={addAutocompleteRef} style={autocompleteContainerStyle}>
                    {addFilteredStocks.map((stock, index) => (
                      <div
                        key={`${stock.symbol}-${stock.exchange}-${index}`}
                        style={autocompleteItemStyle(index === addSelectedIndex)}
                        onClick={() => {
                          setAddSymbol(stock.symbol);
                          setShowAddAutocomplete(false);
                          setAddSelectedIndex(-1);
                          void runSearch(stock.symbol);
                        }}
                        onMouseEnter={() => setAddSelectedIndex(index)}
                      >
                        <div style={autocompleteSymbolStyle}>
                          {stock.symbol} - {stock.exchange}
                        </div>
                        <div style={autocompleteNameStyle}>{stock.name}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {mode !== 'edit' && (
              <button
                type="button"
                style={{ ...searchButtonStyle, justifySelf: 'end' }}
                onClick={() => void runSearch()}
                disabled={addBusy}
              >
                {addBusy ? '...' : 'Search'}
              </button>
            )}
          </div>

          <div style={{ marginLeft: 5 }}>
            <div style={labelStyle}>Buy Price Alert</div>
            <input value={addBuyAlert} onChange={(e) => setAddBuyAlert(e.target.value)} placeholder="" style={inputStyle} />
          </div>

          <div style={{ marginLeft: 5 }}>
            <div style={labelStyle}>Sell Price Alert</div>
            <input value={addSellAlert} onChange={(e) => setAddSellAlert(e.target.value)} placeholder="" style={inputStyle} />
          </div>

          {addError && <div style={{ color: '#ef4444', fontSize: 12, fontWeight: 800 }}>{addError}</div>}
        </div>

        <div style={modalFooterStyle}>
          <button
            type="button"
            style={{
              ...primaryButtonStyle,
              opacity: addBusy ? 0.75 : 1,
              cursor: addBusy ? 'not-allowed' : 'pointer',
            }}
            onClick={() => void save()}
            disabled={addBusy || !addSymbol.trim()}
          >
            {mode === 'edit' ? 'Save' : 'Add'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WatchlistAddEditModal;
